import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Plus, Briefcase, Users, Shield, Eye, UserPlus, 
  DollarSign, Target, Calendar, Activity,
  PlayCircle, CheckCircle, PauseCircle, FileText, Archive,
  LayoutGrid, List
} from 'lucide-react';
import api from '../../lib/api';
import { jobUrlSegment } from '../../lib/slug';
import { Job } from '../../types';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { formatDate } from '../../lib/utils';
import JobAssignmentModal from '../../components/JobAssignmentModal';
import { useAuthStore } from '../../stores/authStore';

export default function JobsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [assignmentViewFilter, setAssignmentViewFilter] = useState('all');
  const [selectedJobForAssignment, setSelectedJobForAssignment] = useState<{ id: string; title: string } | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');

  const jobsQuery = useQuery({
    queryKey: ['agency', 'jobs', 'all'],
    queryFn: async () => {
      const response = await api.get<Job[]>('/jobs');
      return response.data;
    },
    staleTime: 60 * 1000,
  });

  const jobs = jobsQuery.data ?? [];

  const STATUS_CONFIG: Record<string, { icon: any; color: string; bg: string; border: string; label: string }> = {
    DRAFT: {
      icon: FileText,
      color: 'text-glass-text-2',
      bg: 'bg-glass-2',
      border: 'border-glass',
      label: 'Draft',
    },
    OPEN: {
      icon: PlayCircle,
      color: 'text-cyan-500 dark:text-cyan-300',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/20',
      label: 'Open',
    },
    ACTIVE: {
      icon: Activity,
      color: 'text-emerald-500 dark:text-emerald-300',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      label: 'Active',
    },
    PAUSED: {
      icon: PauseCircle,
      color: 'text-amber-500 dark:text-amber-300',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/20',
      label: 'Paused',
    },
    COMPLETED: {
      icon: CheckCircle,
      color: 'text-accent2',
      bg: 'bg-accent2/10',
      border: 'border-accent2/30',
      label: 'Completed',
    },
    ARCHIVED: {
      icon: Archive,
      color: 'text-glass-text-3',
      bg: 'bg-glass-2',
      border: 'border-glass',
      label: 'Archived',
    },
  };

  const ROLE_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
    PRIMARY: { icon: Shield, color: 'text-accent2', label: 'Primary' },
    COLLABORATOR: { icon: Users, color: 'text-cyan-500 dark:text-cyan-300', label: 'Collaborator' },
    REVIEWER: { icon: Eye, color: 'text-emerald-500 dark:text-emerald-300', label: 'Reviewer' },
  };

  const canManageAssignments = ['OWNER', 'ADMIN', 'MANAGER'].includes(user?.roleInOrg || '');

  const jobStatusPill = (status: string) => {
    const s = status.toUpperCase();
    if (s === 'COMPLETED') return 'status-pill status-pill-muted';
    if (['ACTIVE', 'OPEN'].includes(s)) return s === 'OPEN' ? 'status-pill status-pill-info' : 'status-pill status-pill-pos';
    if (['PAUSED', 'DRAFT'].includes(s)) return 'status-pill status-pill-warn';
    return 'status-pill status-pill-muted';
  };

  // Filter jobs based on assignment view
  const filteredJobs = jobs.filter((job: any) => {
    if (statusFilter && job.status !== statusFilter) return false;
    if (assignmentViewFilter === 'all') return true;
    if (assignmentViewFilter === 'my-jobs') {
      return job.assignments?.some((a: any) => a.userId === user?.id);
    }
    if (assignmentViewFilter === 'unassigned') {
      return !job.assignments || job.assignments.length === 0;
    }
    return true;
  });

  const getProgressPercentage = (current: number, target: number) => {
    if (!target || target === 0) return 0;
    return Math.min(Math.round((current / target) * 100), 100);
  };

  const activeJobCount = jobs.filter((job) => job.status === 'ACTIVE').length;
  const draftJobCount = jobs.filter((job) => job.status === 'DRAFT').length;

  if (jobsQuery.isPending) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent2 mx-auto"></div>
          <p className="mt-4 text-glass-text-2">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="page-title text-[1.375rem] md:text-[2.25rem]">Campaigns</h1>
          <p className="text-glass-text-2 text-xs md:text-sm mt-1">
            <span className="md:hidden">{jobs.length} total · {activeJobCount} active</span>
            <span className="hidden md:inline">Manage your campaigns and track progress</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="glass-card hidden md:flex items-center p-1 rounded-xl">
            <button
              onClick={() => setViewMode('card')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                viewMode === 'card'
                  ? 'bg-accent2/15 text-accent2'
                  : 'text-glass-text-2 hover:text-glass-text hover:bg-glass-3'
              }`}
              title="Card View"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="text-sm font-medium">Cards</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-accent2/15 text-accent2'
                  : 'text-glass-text-2 hover:text-glass-text hover:bg-glass-3'
              }`}
              title="List View"
            >
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">List</span>
            </button>
          </div>
          <Button
            data-help-id="campaign.create_button"
            className="h-11 w-11 shrink-0 p-0 md:h-auto md:w-auto md:px-4 md:py-2"
            onClick={() => navigate('/agency/campaigns/new')}
            aria-label="Create campaign"
            title="Create campaign"
          >
            <Plus className="w-5 h-5 md:w-4 md:h-4 md:mr-2" />
            <span className="hidden md:inline">Create campaign</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="md:hidden flex items-center gap-2 min-w-0">
        <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { value: '', label: 'All', count: jobs.length },
            { value: 'ACTIVE', label: 'Active', count: activeJobCount },
            { value: 'DRAFT', label: 'Draft', count: draftJobCount },
          ].map((filter) => {
            const active = statusFilter === filter.value;
            return (
              <button
                key={filter.label}
                type="button"
                onClick={() => setStatusFilter(filter.value)}
                className={`shrink-0 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${
                  active
                    ? 'border-accent2/30 bg-accent2/15 text-accentText'
                    : 'border-glass bg-glass-3 text-glass-text-2 hover:border-glass-strong hover:text-glass-text'
                }`}
                aria-pressed={active}
              >
                {filter.label} {filter.count}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setAssignmentViewFilter((value) => value === 'my-jobs' ? 'all' : 'my-jobs')}
            className={`shrink-0 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${
              assignmentViewFilter === 'my-jobs'
                ? 'border-accent2/30 bg-accent2/15 text-accentText'
                : 'border-glass bg-glass-3 text-glass-text-2 hover:border-glass-strong hover:text-glass-text'
            }`}
            aria-pressed={assignmentViewFilter === 'my-jobs'}
          >
            Mine
          </button>
        </div>
        <button
          type="button"
          onClick={() => setViewMode((mode) => mode === 'card' ? 'list' : 'card')}
          className="glass-card flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg text-glass-text-2 transition-colors hover:border-accent2/40 hover:text-accent2"
          aria-label={viewMode === 'card' ? 'Switch to list view' : 'Switch to card view'}
          title={viewMode === 'card' ? 'List view' : 'Card view'}
        >
          {viewMode === 'card' ? <List className="h-[17px] w-[17px]" /> : <LayoutGrid className="h-[17px] w-[17px]" />}
        </button>
      </div>

      <div className="glass-card hidden p-4 md:block">
        <div className="flex items-center gap-4 flex-wrap">
          <Briefcase className="w-5 h-5 text-glass-text-3" />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-48"
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="OPEN">Open</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="COMPLETED">Completed</option>
            <option value="ARCHIVED">Archived</option>
          </Select>

          <div className="h-6 w-px bg-glass-strong"></div>

          <Users className="w-5 h-5 text-glass-text-3" />
          <Select
            value={assignmentViewFilter}
            onChange={(e) => setAssignmentViewFilter(e.target.value)}
            className="w-48"
          >
            <option value="all">All campaigns</option>
            <option value="my-jobs">My campaigns</option>
            <option value="unassigned">Unassigned</option>
          </Select>
        </div>
      </div>

      {/* Jobs Display - Card or List View */}
      {filteredJobs.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Briefcase className="w-16 h-16 text-glass-text-3 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-glass-text mb-2">No campaigns yet</h3>
          <p className="text-glass-text-3 mb-6">
            Create a campaign, publish it, then invite KOLs from the Invite tab.
          </p>
          <Button onClick={() => navigate('/agency/campaigns/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Create campaign
          </Button>
        </div>
      ) : viewMode === 'card' ? (
        /* Card View — 2 columns on desktop (handoff-style compact cards) */
        <div className="grid grid-cols-1 gap-2.5 md:gap-4 lg:grid-cols-2">{
          filteredJobs.map((job: any) => {
            const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.DRAFT;
            const StatusIcon = statusConfig.icon;
            const broadcastProgress = getProgressPercentage(job.broadcastedCount, job.estimatedKOL);
            const acceptanceRate = job.broadcastedCount > 0 
              ? Math.round((job.acceptedCount / job.broadcastedCount) * 100) 
              : 0;
            const targetCount = Math.max(job.estimatedKOL || 0, 1);
            const acceptedSegment = Math.min((job.acceptedCount / targetCount) * 100, 100);
            const sentSegment = Math.min(
              (Math.max(job.broadcastedCount - job.acceptedCount, 0) / targetCount) * 100,
              100 - acceptedSegment,
            );
            const nextActionOnly =
              job.status === 'DRAFT' ||
              (job.broadcastedCount === 0 && !['COMPLETED', 'ARCHIVED'].includes(job.status));

            return (
              <div
                key={job.id}
                onClick={() => navigate(`/agency/campaigns/${jobUrlSegment(job)}`)}
                className="glass-card p-[13px] md:p-4 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150 cursor-pointer overflow-hidden group flex flex-col"
              >
                {/* Header Row */}
                <div className="flex items-start justify-between gap-2.5 mb-2.5 md:gap-3 md:mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[0.9375rem] font-bold tracking-tight text-glass-text truncate group-hover:text-accent2 transition-colors">
                      {job.title}
                    </h3>
                    <div className="text-xs text-glass-text-2 mt-1 min-w-0">
                      <span className="block truncate md:hidden">
                        {job.brandOrg?.name || 'Unknown Client'} · {formatDate(job.createdAt)}
                      </span>
                      <span className="hidden md:flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1 min-w-0">
                        <Briefcase className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{job.brandOrg?.name || 'Unknown Client'}</span>
                        </span>
                        <span className="text-glass-text-3">·</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          {formatDate(job.createdAt)}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`${jobStatusPill(job.status)} md:hidden`}>
                      {statusConfig.label}
                    </span>
                    <div
                      className={`hidden md:flex items-center gap-1.5 px-2 py-1 rounded-lg border ${statusConfig.border} ${statusConfig.bg}`}
                    >
                      <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                      <span className={`text-[0.7rem] font-semibold ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>

                    {canManageAssignments && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedJobForAssignment({ id: job.id, title: job.title });
                        }}
                        className="btn-accent hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-xs"
                        title="Manage Team"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        <span className="font-medium hidden xl:inline">Manage Team</span>
                      </button>
                    )}
                  </div>
                </div>

                {job.status === 'DRAFT' ? (
                  <p className="text-xs text-amber-500 dark:text-amber-300 mb-2.5 md:mb-3">
                    Draft — open the campaign to publish, then invite KOLs
                  </p>
                ) : job.broadcastedCount === 0 && !['COMPLETED', 'ARCHIVED'].includes(job.status) ? (
                  <p className="text-xs text-accentText mb-2.5 md:mb-3">Published — invite KOLs next</p>
                ) : null}

                {!nextActionOnly && (
                  <div className="md:hidden mb-2.5 space-y-2">
                    <p className="truncate text-[12.5px] text-glass-text-2">
                      <span className="font-semibold text-glass-text">
                        {job.totalBudgetRM ? `RM ${job.totalBudgetRM.toLocaleString()}` : 'Budget TBD'}
                      </span>{' '}
                      · {job.estimatedKOL || 0} target · {job.broadcastedCount || 0} sent · {job.acceptedCount || 0} accepted
                    </p>
                    <div
                      className="flex h-2 overflow-hidden rounded-full bg-glass-3"
                      aria-label={`${Math.round(broadcastProgress)}% invite progress, ${acceptanceRate}% acceptance rate`}
                    >
                      <span className="h-full bg-accent2" style={{ width: `${sentSegment}%` }} />
                      <span className="h-full bg-emerald-500 dark:bg-emerald-400" style={{ width: `${acceptedSegment}%` }} />
                    </div>
                  </div>
                )}

                {/* Metrics — compact 2×2 */}
                <div className="hidden md:grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <div className="bg-glass-2 rounded-[13px] px-2.5 py-2 border border-glass">
                    <div className="flex items-center gap-1 mb-0.5">
                      <DollarSign className="w-3 h-3 text-glass-text-3" />
                      <span className="text-[10px] font-medium text-glass-text-3 uppercase tracking-[0.05em]">
                        Budget
                      </span>
                    </div>
                    <div className="stat-display text-lg text-glass-text truncate">
                      {job.totalBudgetRM ? `RM ${job.totalBudgetRM.toLocaleString()}` : 'TBD'}
                    </div>
                  </div>

                  <div className="bg-glass-2 rounded-[13px] px-2.5 py-2 border border-glass">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Target className="w-3 h-3 text-glass-text-3" />
                      <span className="text-[10px] font-medium text-glass-text-3 uppercase tracking-[0.05em]">
                        Target
                      </span>
                    </div>
                    <div className="stat-display text-lg text-glass-text">
                      {job.estimatedKOL || 0}
                    </div>
                  </div>

                  <div className="bg-accent2/10 rounded-[13px] px-2.5 py-2 border border-accent2/20">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Activity className="w-3 h-3 text-accent2" />
                      <span className="text-[10px] font-medium text-accent2 uppercase tracking-[0.05em]">
                        Sent
                      </span>
                    </div>
                    <div className="stat-display text-lg text-glass-text">
                      {job.broadcastedCount}
                    </div>
                  </div>

                  <div className="bg-emerald-500/10 rounded-[13px] px-2.5 py-2 border border-emerald-500/20">
                    <div className="flex items-center gap-1 mb-0.5">
                      <CheckCircle className="w-3 h-3 text-emerald-500 dark:text-emerald-300" />
                      <span className="text-[10px] font-medium text-emerald-500 dark:text-emerald-300 uppercase tracking-[0.05em]">
                        Accepted
                      </span>
                    </div>
                    <div className="stat-display text-lg text-glass-text">
                      {job.acceptedCount}
                    </div>
                  </div>
                </div>

                {/* Progress Bars */}
                <div className="hidden md:block space-y-2 mb-3 flex-1">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-medium text-glass-text-2">
                        Invite progress
                      </span>
                      <span className="text-[11px] font-semibold text-glass-text">
                        {broadcastProgress}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-glass-3 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent2 transition-all duration-300"
                        style={{ width: `${broadcastProgress}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-medium text-glass-text-2">
                        Acceptance Rate
                      </span>
                      <span className="text-[11px] font-semibold text-glass-text">
                        {acceptanceRate}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-glass-3 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 dark:bg-emerald-400 transition-all duration-300"
                        style={{ width: `${acceptanceRate}%` }}
                      />
                    </div>
                  </div>

                  {job.approvedTalentCount > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-glass-text-2">
                          Approved Content
                        </span>
                        <span className="text-[11px] font-semibold text-glass-text">
                          {job.approvedTalentCount} ready
                        </span>
                      </div>
                      <div className="h-1.5 bg-glass-3 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 dark:bg-cyan-400 transition-all duration-300"
                          style={{
                            width: `${getProgressPercentage(job.approvedTalentCount, job.acceptedCount)}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Assigned Team */}
                <div className="pt-2.5 md:pt-3 border-t border-glass mt-auto">
                  <div className="flex items-center gap-2 md:hidden">
                    {job.assignments && job.assignments.length > 0 ? (
                      <>
                        <div className="flex shrink-0" aria-label={`${job.assignments.length} assigned team members`}>
                          {job.assignments.slice(0, 3).map((assignment: any, index: number) => (
                            <span
                              key={assignment.id}
                              className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-glass bg-gradient-to-br from-accent2 to-cyan-500 text-[10px] font-bold text-white"
                              style={{ marginLeft: index ? '-0.5rem' : undefined }}
                              title={assignment.user.name}
                            >
                              {String(assignment.user.name || '?').trim().charAt(0).toUpperCase()}
                            </span>
                          ))}
                        </div>
                        <span className="min-w-0 flex-1 truncate text-[11.5px] text-glass-text-3">
                          {job.assignments.slice(0, 2).map((assignment: any) => assignment.user.name).join(' · ')}
                          {job.assignments.length > 2 ? ` +${job.assignments.length - 2}` : ''}
                        </span>
                      </>
                    ) : (
                      <span className="min-w-0 flex-1 truncate text-[11.5px] text-glass-text-3">
                        No team members assigned yet
                      </span>
                    )}
                    {canManageAssignments && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedJobForAssignment({ id: job.id, title: job.title });
                        }}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-glass bg-glass-3 text-glass-text-2 transition-colors hover:border-accent2/40 hover:text-accent2"
                        title="Manage team"
                        aria-label={`Manage team for ${job.title}`}
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="hidden md:block">
                    <div className="text-[10px] font-medium text-glass-text-3 uppercase tracking-[0.05em] mb-1.5">
                      Assigned Team
                    </div>
                    {job.assignments && job.assignments.length > 0 ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {job.assignments.map((assignment: any) => {
                          const roleConfig = ROLE_CONFIG[assignment.role];
                          const RoleIcon = roleConfig.icon;
                          return (
                            <div
                              key={assignment.id}
                              className="inline-flex items-center gap-1 bg-glass-2 border border-glass rounded-full px-2 py-1 hover:border-glass-strong transition-colors"
                              title={`${roleConfig.label}: ${assignment.user.name}`}
                            >
                              <RoleIcon className={`w-3 h-3 ${roleConfig.color}`} />
                              <span className="text-[11px] font-medium text-glass-text">
                                {assignment.user.name}
                              </span>
                              <span className={`text-[10px] ${roleConfig.color}`}>
                                ({roleConfig.label})
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-glass-text-3">
                        <Users className="w-3.5 h-3.5" />
                        <span>No team members assigned yet</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        }</div>
      ) : (
        /* List View */
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto overscroll-x-contain">
          <table className="w-full min-w-[64rem]">
            <thead className="bg-glass-3 border-b border-glass">
              <tr>
                {['Campaign', 'Status', 'Team', 'Progress', 'Budget', 'Target', 'Created'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-glass-text-2 uppercase tracking-[0.05em]"
                  >
                    {h}
                  </th>
                ))}
                {canManageAssignments && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {filteredJobs.map((job: any) => {
                const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.DRAFT;

                return (
                  <tr
                    key={job.id}
                    onClick={() => navigate(`/agency/campaigns/${jobUrlSegment(job)}`)}
                    className="hover:bg-glass-2 cursor-pointer transition-colors"
                  >
                    {/* Job */}
                    <td className="px-6 py-4">
                      <div className="font-semibold text-glass-text">{job.title}</div>
                      <div className="text-sm text-glass-text-3">{job.brandOrg?.name}</div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={jobStatusPill(job.status)}>{statusConfig.label}</span>
                    </td>

                    {/* Team */}
                    <td className="px-6 py-4">
                      {job.assignments && job.assignments.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          {job.assignments.slice(0, 2).map((assignment: any) => {
                            const roleConfig = ROLE_CONFIG[assignment.role];
                            const RoleIcon = roleConfig.icon;
                            return (
                              <div
                                key={assignment.id}
                                className="inline-flex items-center gap-1 bg-glass-3 rounded px-2 py-0.5"
                                title={`${assignment.user.name} - ${roleConfig.label}`}
                              >
                                <RoleIcon className={`w-3 h-3 ${roleConfig.color}`} />
                                <span className="text-xs text-glass-text-2">
                                  {assignment.user.name.split(' ')[0]}
                                </span>
                              </div>
                            );
                          })}
                          {job.assignments.length > 2 && (
                            <span className="text-xs text-glass-text-3">
                              +{job.assignments.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-glass-text-3 italic">Unassigned</span>
                      )}
                    </td>

                    {/* Progress */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 text-sm">
                        <div>
                          <span className="font-medium text-glass-text">
                            {job.broadcastedCount}
                          </span>
                          <span className="text-glass-text-3 text-xs ml-0.5">sent</span>
                        </div>
                        <div className="text-glass-text-3">•</div>
                        <div>
                          <span className="font-medium text-emerald-500 dark:text-emerald-300">
                            {job.acceptedCount}
                          </span>
                          <span className="text-glass-text-3 text-xs ml-0.5">accepted</span>
                        </div>
                      </div>
                    </td>

                    {/* Budget */}
                    <td className="px-6 py-4 text-sm text-glass-text font-medium">
                      {job.totalBudgetRM ? `RM ${job.totalBudgetRM.toLocaleString()}` : '-'}
                    </td>

                    {/* Target */}
                    <td className="px-6 py-4 text-sm text-glass-text-2">
                      {job.estimatedKOL || '-'} KOLs
                    </td>

                    {/* Created */}
                    <td className="px-6 py-4 text-sm text-glass-text-3">
                      {formatDate(job.createdAt)}
                    </td>

                    {/* Actions */}
                    {canManageAssignments && (
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedJobForAssignment({ id: job.id, title: job.title });
                          }}
                          className="btn-accent inline-flex items-center gap-1.5 px-3 py-1.5 text-sm"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          <span>Manage</span>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {selectedJobForAssignment && (
        <JobAssignmentModal
          jobId={selectedJobForAssignment.id}
          jobTitle={selectedJobForAssignment.title}
          onClose={() => setSelectedJobForAssignment(null)}
          onAssignmentChange={() => {
            void queryClient.invalidateQueries({ queryKey: ['agency', 'jobs'] });
          }}
        />
      )}
    </div>
  );
}
